'use client';

import { PhoneIcon } from '@chakra-ui/icons';
import {
  Box,
  Container,
  Divider,
  Flex,
  Heading,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import {
  Suspense,
  useMemo,
} from 'react';

import { ReactComponent as MapLocationIcon } from '@/app/_icons/CkMapIcon.svg';
import {
  DealershipContactInfo,
  DealershipProps,
  DisplayOperatingHour,
} from '@/types/dealership/dealership.types';

import {
  formatDealerPhoneNumberForTel,
  groupOperatingHours,
} from './dealershipDateUtils';
import styles from './FindUsSection.module.scss';

interface Props {
  dealerInfo: DealershipContactInfo | DealershipProps;
}

const FindUsSection = ({ dealerInfo }: Props) => {
  const groupedOperatingHours = useMemo(
    () => groupOperatingHours(dealerInfo?.operatingHours ?? []),
    [dealerInfo?.operatingHours],
  );

  const additionalAddress = dealerInfo?.additionalAddress;

  const additionalGroupedOperatingHours = useMemo(
    () =>
      groupOperatingHours(
        additionalAddress?.operatingHours ?? [],
      ),
    [additionalAddress?.operatingHours],
  );

  const phoneNumberForTel = useMemo(
    () => formatDealerPhoneNumberForTel(dealerInfo?.phoneNumber),
    [dealerInfo?.phoneNumber],
  );

  const additionalPhoneNumberForTel = useMemo(
    () =>
      formatDealerPhoneNumberForTel(additionalAddress?.phoneNumber),
    [additionalAddress?.phoneNumber],
  );

  const hasAdditionalAddress = Boolean(
    dealerInfo?.hasAdditionalAddress && additionalAddress,
  );

  const fullAddress = [
    dealerInfo?.mainAddress,
    dealerInfo?.secondaryAddress,
    dealerInfo?.landmark,
    dealerInfo?.city?.name,
    dealerInfo?.city?.province?.name,
    dealerInfo?.code,
  ]
    .filter(Boolean)
    .join(', ');

  const additionalFullAddress = [
    additionalAddress?.mainAddress,
    additionalAddress?.secondaryAddress,
    additionalAddress?.city?.name,
    additionalAddress?.city?.province?.name,
  ]
    .filter(Boolean)
    .join(', ');

  const renderOperatingHours = (
    hours: DisplayOperatingHour[],
    prefix: string,
  ) =>
    hours.length > 0 ? (
      hours.map((item, index) => {
        const isClosed = item.closed || String(item.openTime ?? '').trim().toLowerCase() === 'closed';

        return (
          <Flex
            key={`${prefix}-${item.day}-${index}`}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1.5}
            width="100%"
          >
            <Text
              as="b"
              fontSize="14px"
              lineHeight="19.6px"
              flexShrink={0}
              mr={2}
            >
              {item.day}
            </Text>

            <Text fontSize="14px" lineHeight="19.6px" textAlign="right">
              {isClosed
                ? 'Closed'
                : `${item.openTime} - ${item.closeTime}`}
            </Text>
          </Flex>
        );
      })
    ) : (
      <Text fontSize="14px" lineHeight="19.6px">
        Operating hours not available.
      </Text>
    );

  const renderAddress = ({
    address,
    googleAddress,
    title,
  }: {
    address: string;
    googleAddress?: string | null;
    title: string;
  }) => (
    <>
      <Heading
        as="h3"
        className={styles.titleMd}
        marginBottom={4}
      >
        {title}
      </Heading>

      <Flex
        as="a"
        gap={3}
        alignItems="flex-start"
        href={
          googleAddress ||
          `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            address,
          )}`
        }
        target="_blank"
        rel="noopener noreferrer"
      >
        <Box mt={1} flexShrink={0}>
          <MapLocationIcon
            className={styles.locationIcon}
            width={28}
            height={28}
          />
        </Box>

        <Text fontSize="14px" lineHeight="19.6px">
          {address || 'Address not available'}
        </Text>
      </Flex>
    </>
  );

  return (
    <Container
      maxW="100%"
      className={styles.mapWrapper}
      paddingX={{ base: '6', md: '14' }}
      paddingTop={{ base: '14', md: '50px' }}
      paddingBottom={{ base: 14, md: '50px' }}
      id="contact-section"
    >
      <Suspense fallback={<Text>Loading contact details...</Text>}>
        <div className={styles.headingElement}>
          <Heading
            as="h2"
            className="titleLg"
            textAlign={{ base: 'center', lg: 'start' }}
            mb={{ base: 8, md: '45px' }}
            ml="70px"
          >
            Find Us
          </Heading>
        </div>

        <Box maxWidth="1440px" margin="auto">
          {/* Default address and showroom hours */}
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacingX={{ base: 0, md: '40px' }}
            spacingY={8}
            className={styles.mapContactDetails}
          >
            {/* Default address and contact numbers */}
            <Box
              className={styles.headingElement}
              width="100%"
              minWidth={0}
            >
              {renderAddress({
                address: fullAddress,
                googleAddress: dealerInfo?.google_address,
                title: hasAdditionalAddress
                  ? dealerInfo?.defaultAddressTitle?.trim() ||
                    'Physical Address'
                  : 'Physical Address',
              })}

              <Box mt={6}>
                <Heading
                  as="h3"
                  className={styles.titleMd}
                  marginBottom={4}
                >
                  Contact Numbers
                </Heading>

                {dealerInfo?.phoneNumber && (
                  <Flex alignItems="center" gap={2} mb={3}>
                    <PhoneIcon
                      className={styles.phoneIcon}
                      width="13px"
                      height="13px"
                    />

                    <Text
                      as="a"
                      href={`tel:+27${phoneNumberForTel}`}
                      fontSize="14px"
                      lineHeight="19.6px"
                    >
                      {dealerInfo.phoneNumber}
                    </Text>
                  </Flex>
                )}

                <Flex
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                >
                  <Text
                    as="b"
                    fontSize="14px"
                    lineHeight="19.6px"
                  >
                    Emergency:
                  </Text>

                  <Text
                    as="a"
                    href="tel:+27861542000"
                    fontSize="14px"
                    lineHeight="19.6px"
                  >
                    086 154 2000
                  </Text>
                </Flex>
              </Box>
            </Box>

            {/* Default showroom hours */}
            <Box
              className={styles.headingElement}
              width="100%"
              minWidth={0}
            >
              <Flex height="100%">
                <Divider
                  orientation="vertical"
                  borderLeftWidth="2px"
                  marginRight={{ base: 0, md: 6 }}
                  className={styles.borderLeft}
                  borderColor="gray.300"
                  display={{ base: 'none', md: 'block' }}
                />

                <Box width="100%" minWidth={0}>
                  <Heading
                    as="h3"
                    className={styles.titleMd}
                    marginBottom={4}
                  >
                    Showroom Hours
                  </Heading>

                  {renderOperatingHours(
                    groupedOperatingHours,
                    'default',
                  )}
                </Box>
              </Flex>
            </Box>
          </SimpleGrid>

          {/* Additional address and its showroom hours */}
          {hasAdditionalAddress && additionalAddress && (
            <Box
              mt={{ base: 8, md: 10 }}
              pt={{ base: 8, md: 10 }}
              borderTopWidth="1px"
              borderTopColor="gray.200"
            >
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacingX={{ base: 0, md: '40px' }}
                spacingY={8}
                className={styles.mapContactDetails}
              >
                {/* Additional address and contact numbers */}
                <Box
                  className={styles.headingElement}
                  width="100%"
                  minWidth={0}
                >
                  {renderAddress({
                    address: additionalFullAddress,
                    googleAddress: additionalAddress.google_address,
                    title:
                      additionalAddress.title?.trim() ||
                      'Additional Physical Address',
                  })}

                  <Box mt={6}>
                    <Heading
                      as="h3"
                      className={styles.titleMd}
                      marginBottom={4}
                    >
                      Contact Numbers
                    </Heading>

                    {additionalAddress.phoneNumber ? (
                      <Flex alignItems="center" gap={2}>
                        <PhoneIcon
                          className={styles.phoneIcon}
                          width="13px"
                          height="13px"
                        />

                        <Text
                          as="a"
                          href={`tel:${additionalPhoneNumberForTel}`}
                          fontSize="14px"
                          lineHeight="19.6px"
                        >
                          {additionalAddress.phoneNumber}
                        </Text>
                      </Flex>
                    ) : (
                      <Text fontSize="14px" lineHeight="19.6px">
                        Contact number not available.
                      </Text>
                    )}
                  </Box>
                </Box>

                {/* Additional showroom hours */}
                <Box
                  className={styles.headingElement}
                  width="100%"
                  minWidth={0}
                >
                  <Flex height="100%">
                    <Divider
                      orientation="vertical"
                      borderLeftWidth="2px"
                      marginRight={{ base: 0, md: 6 }}
                      className={styles.borderLeft}
                      borderColor="gray.300"
                      display={{ base: 'none', md: 'block' }}
                    />

                    <Box width="100%" minWidth={0}>
                      <Heading
                        as="h3"
                        className={styles.titleMd}
                        marginBottom={4}
                      >
                        Showroom Hours
                      </Heading>

                      {renderOperatingHours(
                        additionalGroupedOperatingHours,
                        'additional',
                      )}
                    </Box>
                  </Flex>
                </Box>
              </SimpleGrid>
            </Box>
          )}
        </Box>
      </Suspense>
    </Container>
  );
};

export default FindUsSection;
